import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CommunityWisdom = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWisdom = async () => {
      try {
        const res = await fetch(`${API}/community-wisdom`);
        const data = await res.json();
        setEntries(data.entries || []);
      } catch (err) {
        console.warn('[CommunityWisdom] fetch failed:', err?.message);
      } finally {
        setLoading(false);
      }
    };
    fetchWisdom();
  }, []);

  return (
    <div className="min-h-screen px-4 py-8 relative">
      <div className="max-w-2xl mx-auto relative z-10">
        {/* Back */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate('/')}
          className="flex items-center gap-2 mb-8 text-sm font-medium"
          style={{ color: '#5C5470', fontFamily: "'Nunito', sans-serif" }}
          data-testid="back-button"
        >
          <ArrowLeft size={18} />
          Back to Primordeia
        </motion.button>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <div className="flex items-center justify-center gap-2 mb-3">
            <Users size={28} style={{ color: '#4F378B' }} />
            <h1
              className="text-4xl sm:text-5xl font-bold"
              style={{
                fontFamily: "'Fredoka', sans-serif",
                background: 'linear-gradient(135deg, #4F378B 0%, #D4A574 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
              data-testid="community-title"
            >
              Community Wisdom
            </h1>
          </div>
          <p className="text-sm" style={{ color: '#8B7DA8', fontFamily: "'Nunito', sans-serif" }}>
            Insights from seekers who have graduated their tiers
          </p>
        </motion.div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-12">
            <div className="text-sm" style={{ color: '#8B7DA8' }}>Loading wisdom...</div>
          </div>
        )}

        {/* Empty state */}
        {!loading && entries.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16 rounded-2xl"
            style={{ backgroundColor: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)' }}
            data-testid="empty-wisdom"
          >
            <div className="text-4xl mb-4">&#x1F331;</div>
            <h3 className="text-lg font-bold mb-2" style={{ color: '#4F378B', fontFamily: "'Fredoka', sans-serif" }}>
              The Garden is Quiet
            </h3>
            <p className="text-sm max-w-sm mx-auto" style={{ color: '#8B7DA8', fontFamily: "'Nunito', sans-serif" }}>
              No seekers have completed a tier yet. Be the first to walk the path with Papaya and share your insights with the community.
            </p>
          </motion.div>
        )}

        {/* Entries */}
        <div className="space-y-6">
          {entries.map((entry, idx) => (
            <motion.div
              key={entry.timestamp || `wisdom-${idx}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="rounded-2xl p-6"
              style={{
                backgroundColor: 'rgba(255,255,255,0.85)',
                backdropFilter: 'blur(12px)',
                boxShadow: '0 8px 32px rgba(79,55,139,0.1)'
              }}
              data-testid={`wisdom-entry-${idx}`}
            >
              {/* Tier badge */}
              <div className="flex items-center justify-between mb-4">
                <div
                  className="px-4 py-1.5 rounded-full text-sm font-bold"
                  style={{
                    backgroundColor: '#F0E8F8',
                    color: '#4F378B',
                    fontFamily: "'Fredoka', sans-serif"
                  }}
                >
                  {entry.tier} Tier
                </div>
                <span className="text-xs" style={{ color: '#8B7DA8' }}>
                  {entry.timestamp ? new Date(entry.timestamp).toLocaleDateString() : ''}
                </span>
              </div>

              {/* Insights */}
              <div className="space-y-2 mb-4">
                {entry.insights && entry.insights.map((insight, i) => (
                  <div
                    key={`${entry.timestamp}-insight-${i}`}
                    className="flex gap-3 items-start"
                  >
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5"
                      style={{ backgroundColor: '#D4A574', color: '#FFF', fontFamily: "'Fredoka', sans-serif" }}
                    >
                      {i + 1}
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: '#3D3352', fontFamily: "'Nunito', sans-serif" }}>
                        "{insight.message}"
                      </p>
                      {insight.reason && (
                        <p className="text-xs mt-0.5" style={{ color: '#8B7DA8' }}>
                          {insight.reason}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary */}
              {entry.summary && (
                <div
                  className="p-4 rounded-xl text-sm leading-relaxed"
                  style={{
                    backgroundColor: '#F8F5F0',
                    color: '#5C5470',
                    fontFamily: "'Nunito', sans-serif",
                    borderLeft: '3px solid #D4A574'
                  }}
                >
                  <span className="text-xs font-bold block mb-1" style={{ color: '#4F378B' }}>
                    Papaya's Reflection:
                  </span>
                  {entry.summary}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CommunityWisdom;
